'use client';
import { SESSION_STATUS } from '@/constants/auth';
import { APOLLO_FETCH_POLICY } from '@/constants/apollo';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { TypedDocumentNode } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { useSession } from 'next-auth/react';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import { setDefaultHeader } from '@/publicSearch/graphql/headers';
import useSavedFiltersStore, {
  SavedFilter,
  SavedFilterDraft,
  supportsSavedFilters,
} from '@/publicSearch/store/useSavedFiltersStore';
import { useSearchType } from './useSearchType';
import { LogicalOperator } from '@/components/publicSearch/constants';
import {
  CREATE_SAVED_FILTER,
  CreateSavedFilterResponse,
  CreateSavedFilterVariables,
  DELETE_SAVED_FILTER,
  DeleteSavedFilterResponse,
  DeleteSavedFilterVariables,
  GET_SAVED_FILTERS,
  GetSavedFiltersResponse,
  GetSavedFiltersVariables,
  mapDtoToGridSortModel,
  mapFilterGroupsToDto,
  mapGridSortToDto,
  SavedFilterDto,
  UPDATE_SAVED_FILTER,
  UpdateSavedFilterResponse,
  UpdateSavedFilterVariables,
} from '@/publicSearch/graphql/savedFilters';

function mapSavedFilter(filter: SavedFilterDto): SavedFilter {
  const filterGroups = (filter.filterGroups ?? []).map((g) => ({
    logicalOperator:
      g.logicalOperator === LogicalOperator.or
        ? LogicalOperator.or
        : LogicalOperator.and,
    filters: (g.filters ?? []).map((f) => ({
      field: f.field ?? '',
      operator: f.operator ?? '',
      value: f.value ?? '',
    })),
  }));
  const hasGrouped = filterGroups.some((g) => g.filters.length > 0);
  return {
    id: filter.id,
    searchType: filter.searchType as SavedFilter['searchType'],
    name: filter.name,
    filters: filter.filters,
    searchTerm: hasGrouped ? '' : filter.searchTerm,
    saleDateFilter: filter.saleDateFilter ?? null,
    saleTypeFilterValues: filter.saleTypeFilterValues ?? [],
    gridSortModel: mapDtoToGridSortModel(filter.gridSortModel),
    isAiMode: hasGrouped ? false : (filter.isAiMode ?? false),
    filterGroups,
    groupsOperator: filter.groupsOperator ?? LogicalOperator.or,
    aiSearchPrompt: hasGrouped ? '' : (filter.aiSearchPrompt ?? ''),
    createdAt: new Date(filter.createdAt).getTime(),
  };
}

function toSavedFilterMutationInput(filter: SavedFilterDraft) {
  const hasGrouped = (filter.filterGroups ?? []).some(
    (g) => g.filters.length > 0,
  );
  return {
    searchType: filter.searchType,
    name: filter.name,
    searchTerm: hasGrouped ? '' : filter.searchTerm,
    filters: filter.filters,
    saleDateFilter: filter.saleDateFilter,
    saleTypeFilterValues: filter.saleTypeFilterValues ?? [],
    gridSortModel: mapGridSortToDto(filter.gridSortModel),
    isAiMode: hasGrouped ? false : (filter.isAiMode ?? false),
    filterGroups: mapFilterGroupsToDto(filter.filterGroups ?? []),
    groupsOperator: filter.groupsOperator ?? LogicalOperator.or,
    aiSearchPrompt: hasGrouped ? '' : (filter.aiSearchPrompt ?? ''),
  };
}

export function useSavedFilters() {
  const searchType = useSearchType();
  const { data: session, status } = useSession();
  const orgId = usePublicSearchContext((context) => context.society.orgId);
  const isAuthenticated =
    status === SESSION_STATUS.AUTHENTICATED && !!session?.user?.userGuid;
  const hasSocietyContext = !!orgId;
  const shouldUseServer =
    isAuthenticated && supportsSavedFilters(searchType) && hasSocietyContext;
  const requestContext = useMemo(
    () => ({
      headers: setDefaultHeader(orgId),
    }),
    [orgId],
  );

  const rawLocalSavedFilters = useSavedFiltersStore(
    (state) => state.savedFilters,
  );
  const saveLocalFilter = useSavedFiltersStore((state) => state.saveFilter);
  const updateLocalFilter = useSavedFiltersStore((state) => state.updateFilter);
  const deleteLocalFilter = useSavedFiltersStore((state) => state.deleteFilter);
  const clearLocalFilters = useSavedFiltersStore(
    (state) => state.clearAllFilters,
  );

  const localSavedFilters = useMemo(
    () =>
      rawLocalSavedFilters.filter(
        (filter) => !filter.searchType || filter.searchType === searchType,
      ),
    [rawLocalSavedFilters, searchType],
  );

  const {
    data,
    loading: queryLoading,
    refetch,
  } = useQuery(
    GET_SAVED_FILTERS as TypedDocumentNode<
      GetSavedFiltersResponse,
      GetSavedFiltersVariables
    >,
    {
      variables: { searchType },
      context: requestContext,
      skip: !shouldUseServer,
      fetchPolicy: APOLLO_FETCH_POLICY.NETWORK_ONLY,
      notifyOnNetworkStatusChange: true,
    },
  );

  const prevOrgIdRef = useRef(orgId);
  useEffect(() => {
    if (prevOrgIdRef.current === orgId) return;
    prevOrgIdRef.current = orgId;
    if (!shouldUseServer) return;
    const timer = setTimeout(() => void refetch(), 100);
    return () => clearTimeout(timer);
  }, [orgId, shouldUseServer, refetch]);

  const [createSavedFilterMutation, { loading: createLoading }] = useMutation(
    CREATE_SAVED_FILTER as TypedDocumentNode<
      CreateSavedFilterResponse,
      CreateSavedFilterVariables
    >,
  );
  const [updateSavedFilterMutation, { loading: updateLoading }] = useMutation(
    UPDATE_SAVED_FILTER as TypedDocumentNode<
      UpdateSavedFilterResponse,
      UpdateSavedFilterVariables
    >,
  );
  const [deleteSavedFilterMutation, { loading: deleteLoading }] = useMutation(
    DELETE_SAVED_FILTER as TypedDocumentNode<
      DeleteSavedFilterResponse,
      DeleteSavedFilterVariables
    >,
  );

  const savedFilters = useMemo(
    () =>
      shouldUseServer
        ? (data?.savedFilters ?? []).map(mapSavedFilter)
        : localSavedFilters,
    [data?.savedFilters, localSavedFilters, shouldUseServer],
  );

  const refreshSavedFilters = useCallback(async () => {
    if (!shouldUseServer) return;
    await refetch();
  }, [refetch, shouldUseServer]);

  const saveFilter = useCallback(
    async (filter: SavedFilterDraft) => {
      if (!shouldUseServer) {
        return saveLocalFilter(filter);
      }

      const response = await createSavedFilterMutation({
        variables: {
          input: toSavedFilterMutationInput(filter),
        },
        context: requestContext,
      });

      await refreshSavedFilters();
      return mapSavedFilter(response.data!.createSavedFilter);
    },
    [
      createSavedFilterMutation,
      refreshSavedFilters,
      requestContext,
      saveLocalFilter,
      shouldUseServer,
    ],
  );

  const updateFilter = useCallback(
    async (id: string, updates: SavedFilterDraft) => {
      if (!shouldUseServer) {
        return updateLocalFilter(searchType, id, updates);
      }

      const response = await updateSavedFilterMutation({
        variables: {
          id,
          input: toSavedFilterMutationInput(updates),
        },
        context: requestContext,
      });

      await refreshSavedFilters();
      return mapSavedFilter(response.data!.updateSavedFilter);
    },
    [
      refreshSavedFilters,
      requestContext,
      searchType,
      shouldUseServer,
      updateLocalFilter,
      updateSavedFilterMutation,
    ],
  );

  const deleteFilter = useCallback(
    async (id: string) => {
      if (!shouldUseServer) {
        deleteLocalFilter(searchType, id);
        return;
      }

      await deleteSavedFilterMutation({
        variables: { id },
        context: requestContext,
      });
      await refreshSavedFilters();
    },
    [
      deleteLocalFilter,
      deleteSavedFilterMutation,
      refreshSavedFilters,
      requestContext,
      searchType,
      shouldUseServer,
    ],
  );

  const clearAllFilters = useCallback(async () => {
    if (!shouldUseServer) {
      clearLocalFilters(searchType);
      return;
    }

    await Promise.all(
      savedFilters.map((filter) =>
        deleteSavedFilterMutation({
          variables: { id: filter.id },
          context: requestContext,
        }),
      ),
    );
    await refreshSavedFilters();
  }, [
    clearLocalFilters,
    deleteSavedFilterMutation,
    refreshSavedFilters,
    requestContext,
    savedFilters,
    searchType,
    shouldUseServer,
  ]);

  return {
    savedFilters,
    saveFilter,
    updateFilter,
    deleteFilter,
    clearAllFilters,
    refreshSavedFilters,
    isAuthenticated,
    isServerBacked: shouldUseServer,
    isLoading: queryLoading || createLoading || updateLoading || deleteLoading,
  };
}
